import { useMemo, useEffect } from 'react'
import {
  useTrendingChefSnapshot,
  useTrendingDishSnapshot,
  useVendorProfileSnapshot,
} from '../../generated/prototypeRuntime'
import { useScopeLoaded } from '../../lib/projectionHealth'
import { allowSeedContent, isLiveRuntime } from '../../lib/runtime'
import { isVendorOpen } from '../../lib/vendorSchedule'
import { chefs, chefPlans, dishes, type Chef, type Dish } from './content'
import {
  CHEF_CATEGORY_BY_TYPE,
  enumTail,
  GLYPH_BY_CATEGORY,
  isRealValue,
  isSellingKitchen,
  realOr,
  useChefDirectory,
  useChefVerification,
  useUiDishes,
  useUiStores,
} from './projections'
import { usePublicChefs } from '../../lib/discover'
import { chefPhoto, dishPhoto } from './dishImages'

/**
 * Trending hooks. Reads come from the generated `useTrendingChefSnapshot` /
 * `useTrendingDishSnapshot` stores, which the backend populates from real order
 * demand, resolved against the live chef/dish projections.
 *
 * Content policy (see `allowSeedContent` in lib/runtime): a signed-in session,
 * and any production build, render backend data or an empty rail — never the
 * bundled `content.ts` catalog. The curated slice is a pre-authentication dev
 * affordance only, so a fresh `make dev-local` still shows a populated rail
 * before a backend exists. Components stay shape-stable either way.
 */

export type TrendingSource = 'live' | 'curated'

type TrendingRecord = Record<string, unknown>

/** Rank a trending snapshot by its demand counter, most-demanded first. */
const rankByDemand = (records: readonly TrendingRecord[]): TrendingRecord[] => {
  const demand = (r: TrendingRecord): number =>
    Number(r.order_count ?? r.demand ?? r.count ?? r.rank ?? 0)
  return [...records].sort((a, b) => demand(b) - demand(a))
}

/**
 * Top-N chefs by demand. Live: resolve each trending record's `chef_profile_id`
 * against the vendor projection (falling back to demand-ranked vendors when the
 * trending store is empty), so the rail reflects real chefs. Curated only in
 * the dev pre-auth affordance.
 */
export const useHotChefs = (limit = 4): { records: readonly Chef[]; source: TrendingSource; loading: boolean } => {
  const trending = useTrendingChefSnapshot() as { records: readonly TrendingRecord[] }
  const vendors = useVendorProfileSnapshot() as { records: readonly TrendingRecord[] }
  const directory = useChefDirectory()
  const verification = useChefVerification()
  const { chefs: publicChefs } = usePublicChefs(50)
  const vendorsLoaded = useScopeLoaded('profile', 'vendor_profiles')

  return useMemo(() => {
    const live = isLiveRuntime()
    if (!live && allowSeedContent()) {
      const ranked = [...chefs].sort((a, b) => b.subs - a.subs).slice(0, limit)
      return {
        records: ranked.map((chef) => ({
          ...chef,
          image: chefPhoto(chef.image, chef.cuisine, chef.name),
        })),
        source: 'curated',
        loading: false,
      }
    }

    // Loading only until the snapshot lands: after that, no vendors is an
    // empty rail, not a rail still on its way.
    const loading = live && vendors.records.length === 0 && !vendorsLoaded

    // Real chefs from the vendor projection, keyed by profile id. Chef-type
    // vendors only: marketplace vendors (stores, groceries, pharmacies) share
    // vendor_profiles but belong to the market surface, never this rail.
    const byProfile = new Map<string, Chef>()
    for (const v of vendors.records) {
      const profileId = String(v.profile_id ?? v.id ?? '')
      const category = CHEF_CATEGORY_BY_TYPE[enumTail(v.vendor_type)]
      if (!profileId || !category || !isRealValue(v.display_name)) continue

      // Exclude demo seeds, review seeds, and soft-deleted/purged vendors
      const meta = v.metadata as Record<string, unknown> | undefined
      if (meta && (meta.seed === true || meta.seed === 'true' || meta.review_seed === true || meta.review_seed === 'true' || meta.deleted === true || meta.deleted === 'true')) {
        continue
      }
      const rawName = String(v.display_name).trim()
      if (rawName === 'Deleted kitchen' || rawName === 'Deleted account') {
        continue
      }

      // Exclude suspended, rejected, or revoked kitchens
      const vStatus = enumTail(v.verification_status)
      if (['suspended', 'rejected', 'revoked'].includes(vStatus)) {
        continue
      }
      if (verification.size > 0 && !isSellingKitchen(verification.get(profileId))) {
        continue
      }

      const entry = directory.get(profileId)
      if (entry?.name === 'Deleted account' || entry?.name === 'Deleted kitchen') {
        continue
      }

      byProfile.set(profileId, {
        name: entry?.name ?? rawName,
        glyph: '👨🏾‍🍳',
        image: chefPhoto(String(v.avatar_image_url ?? '')) ?? chefPhoto(entry?.avatar),
        category,
        cuisine: realOr(v.cuisine, 'Kitchen'),
        area: realOr(v.location_label, 'Nearby'),
        tags: [],
        rating: publicChefs.find(chef => chef.id === String(v.id))?.rating || 0,
        subs: 0,
        priceFrom: 0,
        status: isVendorOpen(v) ? 'open' : 'closed',
      })
    }

    // Prefer trending order; fall back to whatever real chefs exist.
    const ordered: Chef[] = []
    for (const t of rankByDemand(trending.records)) {
      const chef = byProfile.get(String(t.chef_profile_id ?? ''))
      if (chef && !ordered.includes(chef)) ordered.push(chef)
    }
    for (const chef of byProfile.values()) {
      if (!ordered.includes(chef)) ordered.push(chef)
    }
    return { records: ordered.slice(0, limit), source: 'live', loading }
  }, [trending.records, vendors.records, vendorsLoaded, directory, verification, publicChefs, limit])
}

/**
 * Top-N dishes by demand. Live: rank the real dish projection (via `useUiDishes`)
 * by trending order, so the rail shows real dishes. Curated only in the dev
 * pre-auth affordance.
 */
export const useHotDishes = (limit = 5): { records: readonly Dish[]; source: TrendingSource; loading: boolean } => {
  const trending = useTrendingDishSnapshot() as { records: readonly TrendingRecord[] }
  const ui = useUiDishes()
  const verification = useChefVerification()
  const dishesLoaded = useScopeLoaded('menu', 'dishes')

  return useMemo(() => {
    const live = isLiveRuntime()
    if (!live && allowSeedContent()) {
      const hotChef = chefPlans.find((p) => p.hot)?.chef
      const ranked = hotChef
        ? [
          ...dishes.filter((d) => d.status === 'available' && d.stock !== 0 && hotChef.startsWith(d.chef)),
          ...dishes.filter((d) => d.status === 'available' && d.stock !== 0 && !hotChef.startsWith(d.chef)),
        ].slice(0, limit)
        : dishes.filter((d) => d.status === 'available' && d.stock !== 0).slice(0, limit)
      return { records: ranked, source: 'curated', loading: false }
    }

    // Loading only until the snapshot lands. Without the scope's own status,
    // an empty catalogue read as "still loading" forever, and so did the home
    // page's featured cards, which wait on this flag.
    const loading = live && ui.records.length === 0 && !dishesLoaded

    // Real available dishes, ordered by trending demand.
    const byId = new Map<string, Dish>()
    for (const d of ui.records) {
      if (d.status !== 'available' || d.stock === 0) continue
      if (verification.size > 0 && d.chefId && !isSellingKitchen(verification.get(d.chefId))) continue
      byId.set(d.id, {
        id: d.id,
        name: d.name,
        chef: d.chef,
        chefGlyph: d.chefGlyph, rating: d.rating,
        glyph: d.glyph,
        image: dishPhoto(d.image, d.category, d.name),
        price: d.price,
        category: d.category,
        stock: d.stock,
        status: d.status,
      })
    }
    const ordered: Dish[] = []
    for (const t of rankByDemand(trending.records)) {
      const dish = byId.get(String(t.dish_id ?? t.id ?? ''))
      if (dish && !ordered.includes(dish)) ordered.push(dish)
    }
    for (const dish of byId.values()) {
      if (!ordered.includes(dish)) ordered.push(dish)
    }
    return { records: ordered.slice(0, limit), source: 'live', loading }
  }, [trending.records, ui.records, dishesLoaded, verification, limit])
}

/* ── weekly featured (live home-page promo cards) ─────────────────────── */

const CATEGORY_LABEL: Record<string, string> = {
  rice: 'Rice',
  soups: 'Soups',
  protein: 'Protein',
  breakfast: 'Breakfast',
  grill: 'Grill',
}

export interface WeeklyFeatured {
  /** Top dish of the week with its leading categories. */
  special?: {
    title: string
    subtitle: string
    motif: string
    image?: string
    category?: string
    price?: number
    prepTime?: string
    rating?: string
  }
  /** Live marketplace summary (real store count + categories on offer). */
  market?: { title: string; subtitle: string }
  /** Most-demanded chef, spotlighted. */
  chefOfWeek?: {
    name: string
    subtitle: string
    glyph: string
    image?: string
    rating?: string
  }
  loading: boolean
}

/**
 * Demand-derived promo cards for the customer home page. Where the dev
 * pre-auth affordance shows curated copy (homeSpecial / marketSummary /
 * spotlightPlan), a live session features what the week's real orders say:
 * the top dish and its leading categories, the real store count, and the
 * most-demanded chef. Every card derives from projections already on screen,
 * so an empty deployment simply renders no cards — never invented copy.
 */
export const useWeeklyFeatured = (): WeeklyFeatured => {
  const hotDishes = useHotDishes(8)
  const hotChefs = useHotChefs(1)
  const stores = useUiStores()

  return useMemo(() => {
    const featured: WeeklyFeatured = {
      loading: hotDishes.loading || hotChefs.loading,
    }

    const topDish = hotDishes.records[0]
    if (topDish) {
      // Leading categories in demand order, de-duplicated.
      const cats: string[] = []
      for (const d of hotDishes.records) {
        const label = CATEGORY_LABEL[d.category]
        if (label && !cats.includes(label)) cats.push(label)
      }
      const lead =
        cats.length >= 2
          ? `${cats[0]} & ${cats[1].toLowerCase()} lead this week`
          : cats.length === 1
            ? `${cats[0]} leads this week`
            : 'Fresh from the kitchen'
      featured.special = {
        title: topDish.name,
        subtitle: `${lead} · by ${topDish.chef}`,
        motif: GLYPH_BY_CATEGORY[topDish.category] ?? '🍲',
        image: topDish.image,
        category: topDish.category,
        price: topDish.price,
        prepTime: '15–20m',
        rating: topDish.rating ? topDish.rating.toFixed(1) : undefined,
      }
    }

    const storeCount = stores.records.length
    if (storeCount > 0) {
      const kinds: string[] = []
      for (const s of stores.records) {
        if (!kinds.includes(s.category)) kinds.push(s.category)
      }
      featured.market = {
        title: `${storeCount} ${storeCount === 1 ? 'store' : 'stores'} · ${kinds.slice(0, 2).join(' & ')}`,
        subtitle: 'Everyday essentials, delivered alongside your chow.',
      }
    }

    const topChef = hotChefs.records[0]
    if (topChef) {
      const open = topChef.status === 'open' ? 'Open now' : 'Back soon'
      featured.chefOfWeek = {
        name: topChef.name,
        subtitle: `${topChef.cuisine} · ${topChef.area} · ${open}`,
        glyph: topChef.glyph,
        image: topChef.image,
        rating: topChef.rating ? String(topChef.rating) : undefined,
      }
    }

    featured.loading = hotDishes.loading || hotChefs.loading

    return featured
  }, [hotDishes.records, hotDishes.loading, hotChefs.records, hotChefs.loading, stores.records])
}

/**
 * Hydrates the prototype stores for trending chefs and dishes.
 * This should be called once near the root of the app so the feed renders live data.
 */
export const useHydrateTrendingFeed = () => {
  useEffect(() => {
    if (!isLiveRuntime()) return

    const hydrate = async () => {
      try {
        // We import commands dynamically to avoid circular dependencies if runtime imports trending
        const { commands } = await import('../../lib/runtime')
        const { prototypeStoreRegistry } = await import('../../generated/prototypeRuntime')

        const [chefsRes, dishesRes] = await Promise.all([
          commands.menu.listTrendingChefs(),
          commands.menu.listTrendingDishes(),
        ])

        const chefStore = prototypeStoreRegistry.get('menu.trending_chefs') as { replace: (r: unknown[]) => void } | undefined
        const chefsData = chefsRes as { chefs?: unknown[] } | null | undefined
        if (chefStore && chefsData?.chefs) {
          chefStore.replace(chefsData.chefs)
        }

        const dishStore = prototypeStoreRegistry.get('menu.trending_dishes') as { replace: (r: unknown[]) => void } | undefined
        const dishesData = dishesRes as { dishes?: unknown[] } | null | undefined
        if (dishStore && dishesData?.dishes) {
          dishStore.replace(dishesData.dishes)
        }
      } catch (err: unknown) {
        const error = err as { name?: string; message?: string } | null | undefined
        if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
          return
        }
        console.warn('Failed to hydrate trending feed:', err)
      }
    }

    hydrate()
    
    const onFocusOrVisible = () => {
      if (document.visibilityState === 'visible') {
        hydrate()
      }
    }
    
    document.addEventListener('visibilitychange', onFocusOrVisible)
    window.addEventListener('focus', onFocusOrVisible)
    
    return () => {
      document.removeEventListener('visibilitychange', onFocusOrVisible)
      window.removeEventListener('focus', onFocusOrVisible)
    }
  }, [])
}
